/**
 * AIF-05 — a popover tree/cascader picker over one catalogue axis (type or
 * location). Used by the AI Mapping row editor for both the Defect type
 * (required, leaves = `defect_type`) and the Location (optional, clearable,
 * leaves = `location`) selection.
 *
 * Behaviour (mockup §11.4):
 *  - the whole axis tree is shown (group → leaf); only LEAF nodes are
 *    selectable (groups expand/collapse);
 *  - the chosen value is rendered as the full breadcrumb path so same-named
 *    leaves under different groups are distinguishable (AC2);
 *  - a free-text search filters the tree to nodes whose own name or any
 *    descendant matches;
 *  - the location picker may soft-warn (greyed + ⚠) on invalid (location, type)
 *    pairs via `invalidIds` — never blocks selection (resolved BE: soft-warn).
 *
 * Reuses `buildAxisTree` (the same tree the Trees tab builds) so the picker and
 * the editor agree on structure + ordering.
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { DefectCatalogueEntryV2 } from "@aeriosiq/types";
import { buildAxisTree, type TreeNode } from "./treeBuild.js";
import { locText } from "./locText.js";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  TriangleAlertIcon,
  XIcon,
} from "@aeriosiq/ui/icons";

export interface TreePickerProps {
  /** Visible label / aria prefix, e.g. "Defect type" or "Location". */
  label: string;
  axis: "type" | "location";
  entries: DefectCatalogueEntryV2[];
  /** Currently-selected leaf entryId (or null). */
  value: string | null;
  /** Rendered breadcrumb path for the current value (full group path). */
  valuePath: string[];
  onSelect: (entryId: string) => void;
  /** Optional clear affordance (location picker only). */
  onClear?: (() => void) | undefined;
  /** Disabled (published version) — opens nothing. */
  disabled?: boolean;
  /**
   * Soft-warn set: leaf ids that form an invalid (location, type) pair for the
   * chosen type entry. Rendered greyed + ⚠ but still selectable (never blocks).
   */
  invalidIds?: ReadonlySet<string> | undefined;
  /** Placeholder when nothing is selected. */
  placeholder?: string;
}

function isLeaf(node: TreeNode): boolean {
  return node.children.length === 0;
}

/** A node matches the query if its own name matches; a branch is kept if any
 *  descendant matches (so the path to a hit stays visible). */
function nodeMatches(node: TreeNode, q: string): boolean {
  if (q.length === 0) return true;
  if (locText(node.entry.name).toLowerCase().includes(q)) return true;
  return node.children.some((c) => nodeMatches(c, q));
}

interface RowProps {
  node: TreeNode;
  depth: number;
  q: string;
  value: string | null;
  invalidIds: ReadonlySet<string> | undefined;
  expanded: Set<string>;
  toggle: (id: string) => void;
  onPick: (id: string) => void;
}

function PickerRow({
  node,
  depth,
  q,
  value,
  invalidIds,
  expanded,
  toggle,
  onPick,
}: RowProps): ReactElement | null {
  if (!nodeMatches(node, q)) return null;
  const { entry, children } = node;
  const leaf = isLeaf(node);
  // With an active search, auto-expand branches so matches are reachable.
  const isOpen = q.length > 0 || expanded.has(entry.entryId);
  const selected = value === entry.entryId;
  const invalid = leaf && (invalidIds?.has(entry.entryId) ?? false);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", pl: depth * 2 }}>
        {leaf ? (
          <Box sx={{ width: 28, flexShrink: 0 }} />
        ) : (
          <IconButton
            size="small"
            aria-label={isOpen ? "Collapse" : "Expand"}
            onClick={() => {
              toggle(entry.entryId);
            }}
          >
            {isOpen ? (
              <ChevronDownIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        )}
        {leaf ? (
          <Box
            component="button"
            type="button"
            aria-label={`Select ${locText(entry.name)}`}
            aria-pressed={selected}
            onClick={() => {
              onPick(entry.entryId);
            }}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              textAlign: "left",
              border: "none",
              background: selected ? "action.selected" : "transparent",
              bgcolor: selected ? "action.selected" : "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 14,
              color: invalid ? "text.disabled" : "text.primary",
              borderRadius: 1,
              py: 0.5,
              px: 0.5,
            }}
          >
            {invalid ? (
              <Tooltip title="Invalid pair for the chosen type (validity overlay) — allowed, but check this is intended.">
                <TriangleAlertIcon
                  fontSize="small"
                  color="warning"
                  aria-label="Invalid pair warning"
                />
              </Tooltip>
            ) : null}
            <span>{locText(entry.name)}</span>
            {selected ? <span aria-hidden> ✓</span> : null}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{ flex: 1, py: 0.5, color: "text.secondary", fontWeight: 600 }}
          >
            {locText(entry.name)}
          </Typography>
        )}
      </Box>
      {!leaf && isOpen
        ? children.map((c) => (
            <PickerRow
              key={c.entry.entryId}
              node={c}
              depth={depth + 1}
              q={q}
              value={value}
              invalidIds={invalidIds}
              expanded={expanded}
              toggle={toggle}
              onPick={onPick}
            />
          ))
        : null}
    </Box>
  );
}

export function TreePicker(props: TreePickerProps): ReactElement {
  const {
    label,
    axis,
    entries,
    value,
    valuePath,
    onSelect,
    onClear,
    disabled,
    invalidIds,
    placeholder = "(none)",
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const tree = useMemo(() => buildAxisTree(entries, axis), [entries, axis]);

  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function close(): void {
    setAnchorEl(null);
    setQ("");
  }

  const display = valuePath.length > 0 ? valuePath.join(" › ") : placeholder;
  const open = anchorEl !== null;

  return (
    <Box>
      <Stack direction="row" spacing={0.5} alignItems="stretch">
        <Button
          variant="outlined"
          size="small"
          disabled={disabled ?? false}
          aria-label={`${label}: ${display}`}
          aria-haspopup="dialog"
          endIcon={<ChevronDownIcon />}
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
          sx={{
            justifyContent: "space-between",
            flex: 1,
            textTransform: "none",
            fontWeight: 400,
            color: value === null ? "text.secondary" : "text.primary",
            minWidth: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {display}
          </Box>
        </Button>
        {onClear && value !== null ? (
          <IconButton
            size="small"
            aria-label={`Clear ${label}`}
            disabled={disabled ?? false}
            onClick={() => {
              onClear();
            }}
          >
            <XIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": `Pick ${label}`,
            sx: { width: 360, maxHeight: 420, p: 1 },
          },
        }}
      >
        <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
          <TextField
            size="small"
            placeholder="Search…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value.toLowerCase());
            }}
            slotProps={{ htmlInput: { "aria-label": `Search ${label}` } }}
          />
          <Box sx={{ overflowY: "auto", minHeight: 0 }}>
            {tree.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                No {axis} entries.
              </Typography>
            ) : (
              tree.map((n) => (
                <PickerRow
                  key={n.entry.entryId}
                  node={n}
                  depth={0}
                  q={q}
                  value={value}
                  invalidIds={invalidIds}
                  expanded={expanded}
                  toggle={toggle}
                  onPick={(id) => {
                    onSelect(id);
                    close();
                  }}
                />
              ))
            )}
          </Box>
        </Stack>
      </Popover>
    </Box>
  );
}
